import type { Permission } from "@pax/api-contract/common";
import type { QueryClient } from "@tanstack/react-query";
import { redirect, type StaticDataRouteOption } from "@tanstack/react-router";
import { requireAnyPermission } from "./permissions";
import { type Session, sessionQuery } from "./queries";

/**
 * Who can open a page under `_authenticated`, beyond the signed-in session every page there needs:
 * `"account"` rules out guests, and `anyOf` admits staff holding at least one of the permissions.
 */
type Access = "account" | { anyOf: readonly Permission[] };

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /** Read only by `_authenticated`'s guard, so it means nothing on a page outside it. */
    access?: Access;
  }
}

/** What `_authenticated`'s `beforeLoad` hands its guard. */
interface GuardArgs {
  context: { queryClient: QueryClient };
  location: { href: string };
  matches: readonly { staticData: StaticDataRouteOption }[];
}

/**
 * `_authenticated`'s guard, the one place a page's `staticData.access` is enforced. A signed-out
 * visitor is sent to sign in and back, with accounts only when the page rules out guests. A guest
 * on an account-only page is sent to create one, keeping what they made, and back. A page listing
 * permissions throws `NoAccess` for anyone holding none of them, guests included, since an account
 * alone wouldn't let them in. The session becomes route context, so every page under
 * `_authenticated` reads one that is never null.
 */
export async function requireAccess({
  context: { queryClient },
  location,
  matches,
}: GuardArgs): Promise<{ session: Session }> {
  const access = matches.at(-1)?.staticData.access;
  const session: Session | null = await queryClient.query(sessionQuery);
  if (!session) {
    throw redirect({
      to: "/login",
      search: { next: location.href, requires: access ? "account" : undefined },
    });
  }
  if (access === "account" && session.user.isAnonymous) {
    throw redirect({ to: "/create-account", search: { next: location.href } });
  }
  if (typeof access === "object") await requireAnyPermission(queryClient, session, access.anyOf);
  return { session };
}
